import { useEffect, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import { LoadingContainer } from '../../LoadingContainer'
import { Spinner } from '../../LoadingContainer/Spinner'
import { Success } from '..'
import AssignTicketsModalContentV2 from '../../../providers/AssignToProvider/AssignTicketsModalContent/AssignTicketsModalContentV2'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import {
  addSeasonPackageOrderSeats,
  getPackageMap,
} from '../../../utilities/api'

import RelocatePackageSeatsMap from '../RelocateSeasonSeats/RelocatePackageSeatsMap'
import { buildHoldMap } from '../RelocateSeasonSeats/relocatePackageSeatsMapHelpers'

const MAX_ADDITIONAL_SEATS = 50

const countSelectedSeats = (selectedSeatIds = {}) => {
  let total = 0
  if (selectedSeatIds?.seats) {
    total += Object.keys(selectedSeatIds.seats).length
  }
  if (selectedSeatIds?.gaSeats) {
    Object.values(selectedSeatIds.gaSeats).forEach((seatIds) => {
      total += Array.isArray(seatIds) ? seatIds.length : 0
    })
  }
  return total
}

const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

const buildSeatDataFromMap = (selectedSeatIds = {}, mapData = {}) => {
  const seats = {}
  const seatIds = [
    ...Object.keys(selectedSeatIds?.seats || {}),
    ...Object.values(selectedSeatIds?.gaSeats || {}).flat(),
  ]

  seatIds.forEach((seatId) => {
    const normalizedId = normalizeSeatId(seatId)
    const seatInfo = mapData?.seats?.[normalizedId] || mapData?.seats?.[seatId]
    if (seatInfo) {
      seats[normalizedId] = seatInfo
    }
  })

  return { seats }
}

function parseOrderDetails(details) {
  if (!details) return {}
  if (typeof details === 'string') {
    try {
      return JSON.parse(details)
    } catch {
      return {}
    }
  }
  return details
}

function getSourceOrderCustomFeeIds(order) {
  const details = parseOrderDetails(order?.details)
  const fromDetails = details?.customFeeIds
  if (Array.isArray(fromDetails) && fromDetails.length) {
    return fromDetails
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0)
  }

  const fromRelation = order?.custom_fees
  if (Array.isArray(fromRelation) && fromRelation.length) {
    return fromRelation
      .map((fee) => Number(fee?.id ?? fee?.attributes?.id))
      .filter((n) => Number.isFinite(n) && n > 0)
  }

  return []
}

function getSourceOrderBuyerTypeId(order) {
  const details = parseOrderDetails(order?.details)
  const fromDetails = details?.selectedBuyerType
  if (fromDetails != null && String(fromDetails).trim() !== '') {
    const id = Number(fromDetails)
    if (Number.isFinite(id) && id > 0) return id
  }

  const buyerType = order?.buyer_type
  if (buyerType != null) {
    const id = Number(
      typeof buyerType === 'object'
        ? (buyerType?.id ?? buyerType?.data?.id)
        : buyerType
    )
    if (Number.isFinite(id) && id > 0) return id
  }

  return undefined
}

export default function AddSeasonSeats({ order, onSuccess }) {
  const { closeModal, openModal } = useModal()

  const [step, setStep] = useState('select-seats')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [alert, setAlert] = useState({ show: false, message: '' })

  const [eventPackage, setEventPackage] = useState(null)
  const [mapData, setMapData] = useState(null)
  const [background, setBackground] = useState(null)
  const [soldMap, setSoldMap] = useState(new Map())
  const [reservedMap, setReservedMap] = useState(new Map())
  const [holdMap, setHoldMap] = useState(new Map())
  const [selectedSeatIds, setSelectedSeatIds] = useState({})

  const selectedCount = countSelectedSeats(selectedSeatIds)

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const res = await getPackageMap(order?.package?.uuid, {
          includeSeatIdentity: false,
        })
        const {
          eventPackage: loadedPackage,
          soldSeatIds,
          reservedSeatIds,
          heldSeatIds,
          heldSeatInfoById,
          seatIdentityById,
        } = res?.data || {}

        setEventPackage(loadedPackage)
        setMapData(loadedPackage?.seatmap?.mapping || null)
        setBackground(loadedPackage?.seatmap?.background || null)

        const soldEntries = Array.from(soldSeatIds || [])
          .map((seatId) => normalizeSeatId(seatId))
          .filter(Boolean)
          .map((seatId) => [seatId, seatIdentityById?.[seatId] || true])
        const reservedEntries = Array.from(reservedSeatIds || [])
          .map((seatId) => normalizeSeatId(seatId))
          .filter(Boolean)
          .map((seatId) => [seatId, seatIdentityById?.[seatId] || true])

        const sold = new Map(soldEntries)
        const reserved = new Map(reservedEntries)

        ;(order?.package_tickets || []).forEach((ticket) => {
          const seatId = normalizeSeatId(ticket?.seatId)
          if (seatId) sold.set(seatId, true)
        })

        setSoldMap(sold)
        setReservedMap(reserved)
        setHoldMap(buildHoldMap(heldSeatIds, heldSeatInfoById))
        setSelectedSeatIds({})
      } catch (err) {
        setAlert({
          show: true,
          message:
            err?.response?.data?.error?.message ||
            'Could not load package seat map. Please try again.',
        })
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [order?.id, order?.package?.uuid, order?.package_tickets])

  const openPaymentChoice = () => {
    if (selectedCount <= 0) return
    setAlert({ show: false, message: '' })
    setStep('payment-choice')
  }

  const addSeatsWithoutPayment = async () => {
    try {
      setIsSaving(true)
      setAlert({ show: false, message: '' })

      await addSeasonPackageOrderSeats({
        orderId: order?.id,
        selectedSeats: selectedSeatIds,
        price: 0,
      })

      closeModal()
      openModal(
        Success,
        {
          title: 'Seats added successfully',
          description: `${selectedCount} seat${selectedCount === 1 ? '' : 's'} added to this order without collecting payment.`,
          onClose: () => {
            closeModal()
            if (typeof onSuccess === 'function') onSuccess()
          },
        },
        { className: 'modal-md', contentClassName: '' },
        { replace: true }
      )
    } catch (err) {
      setAlert({
        show: true,
        message:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Unable to add seats. Please try again.',
      })
      setStep('select-seats')
    } finally {
      setIsSaving(false)
    }
  }

  const openPaymentStep = () => {
    if (selectedCount <= 0) return

    const renewPackage = {
      ...(order?.package || {}),
      uuid:
        order?.package?.uuid ||
        order?.packageUuid ||
        order?.package_uuid ||
        order?.packageUUID,
    }

    const seatData = buildSeatDataFromMap(selectedSeatIds, mapData)
    const initialAssignTo = {
      firstName:
        order?.firstName || order?.users_permissions_user?.firstName || '',
      lastName:
        order?.lastName || order?.users_permissions_user?.lastName || '',
      email: order?.email || order?.users_permissions_user?.email || '',
      phoneNumber:
        order?.phoneNumber ||
        order?.users_permissions_user?.phoneNumber ||
        null,
      organizationName: order?.purchasedByOrganizationName || '',
    }
    const initialExistingUser = initialAssignTo?.email
      ? {
          label:
            `${initialAssignTo.firstName} ${initialAssignTo.lastName}`.trim(),
          firstName: initialAssignTo.firstName,
          lastName: initialAssignTo.lastName,
          email: initialAssignTo.email,
          phoneNumber: initialAssignTo.phoneNumber,
          custom: initialAssignTo.email,
        }
      : undefined

    const sourceOrderPricingTier =
      order?.pricingTier ||
      order?.pricing_tier ||
      order?.details?.pricingTier ||
      order?.details?.pricing_tier ||
      parseOrderDetails(order?.details)?.pricingTier ||
      parseOrderDetails(order?.details)?.pricing_tier

    closeModal()
    openModal(
      AssignTicketsModalContentV2,
      {
        eventPackage: renewPackage,
        selectedSeatIds,
        data: seatData,
        initialAssignTo,
        initialExistingUser,
        addSeatsToOrderId: order?.id,
        sourceOrderPricingTier,
        sourceOrderCustomFeeIds: getSourceOrderCustomFeeIds(order),
        sourceOrderBuyerTypeId: getSourceOrderBuyerTypeId(order),
        reloadAssignments: onSuccess,
      },
      { className: 'modal-xl', contentClassName: 'h-md' },
      { replace: true }
    )
  }

  if (isLoading) {
    return (
      <Modal.Body>
        <LoadingContainer />
      </Modal.Body>
    )
  }

  if (step === 'payment-choice') {
    return (
      <Modal.Body className="d-flex-column">
        <div className="modal-body-heading">
          <div className="section-heading section-heading--secondary">
            <h1 className="mt-2 mb-0">Collect payment?</h1>
            <p className="section-header-desc mt-1 mb-0">
              You selected {selectedCount} additional seat
              {selectedCount === 1 ? '' : 's'}. Do you want to collect payment
              for these seats, or add them to the order without charging?
            </p>
          </div>
        </div>

        {alert.show && (
          <Alert variant="danger" className="mb-3">
            <p className="mb-0">{alert.message}</p>
          </Alert>
        )}

        <Stack
          direction="horizontal"
          className="btn-group-flex justify-content-end mt-4"
        >
          <Button
            variant="default"
            size="lg"
            disabled={isSaving}
            onClick={() => setStep('select-seats')}
          >
            Back
          </Button>
          <Button
            variant="outline-light"
            size="lg"
            disabled={isSaving}
            onClick={addSeatsWithoutPayment}
          >
            {isSaving ? <Spinner variant="dark" /> : 'Add without payment'}
          </Button>
          <Button size="lg" disabled={isSaving} onClick={openPaymentStep}>
            Collect payment
          </Button>
        </Stack>
      </Modal.Body>
    )
  }

  return (
    <Modal.Body className="d-flex-column">
      <div className="modal-body-heading">
        <div className="section-heading section-heading--secondary">
          <h1 className="mt-2 mb-0">Add Seats</h1>
          <p className="section-header-desc mt-1 mb-0">
            Select additional available seats on the map to add to this season
            ticket order.
          </p>
        </div>
      </div>

      {alert.show && (
        <Alert variant="danger" className="mb-3">
          <p className="mb-0">{alert.message}</p>
        </Alert>
      )}

      <Stack gap={3}>
        <div className="small">
          New seats selected: <strong>{selectedCount}</strong>
        </div>

        {mapData && background ? (
          <RelocatePackageSeatsMap
            data={mapData}
            background={background}
            eventPackage={eventPackage}
            selectedSeatIds={selectedSeatIds}
            setSelectedSeatIds={setSelectedSeatIds}
            soldMap={soldMap}
            reservedMap={reservedMap}
            holdMap={holdMap}
            maxSelectable={MAX_ADDITIONAL_SEATS}
            restrictToPackageInventory={false}
          />
        ) : (
          <Alert variant="light" className="mb-0">
            <p className="mb-0">Seat map is unavailable for this package.</p>
          </Alert>
        )}
      </Stack>

      <Stack
        direction="horizontal"
        className="btn-group-flex justify-content-end mt-4"
      >
        <Button variant="default" size="lg" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          size="lg"
          disabled={selectedCount <= 0}
          onClick={openPaymentChoice}
        >
          Continue
        </Button>
      </Stack>
    </Modal.Body>
  )
}
